// display.kpi-stat.v1
//
// A single KPI tile — label + value + optional delta + optional trend
// hint. Used in dashboards and analytics pages. Composes with Card
// when you need a bordered variant.

import { type JSX, Show, splitProps } from "solid-js"

export type KpiTrend = "up" | "down" | "flat"

export interface KpiStatProps {
  label: string
  value: JSX.Element
  delta?: string
  trend?: KpiTrend
  hint?: string
  class?: string
}

const trendClass = {
  up: "text-icon-success-active",
  down: "text-icon-critical-base",
  flat: "text-text-base",
} as const satisfies Record<KpiTrend, string>

const trendGlyph = {
  up: "↑",
  down: "↓",
  flat: "→",
} as const satisfies Record<KpiTrend, string>

export function KpiStat(props: KpiStatProps) {
  const [local, rest] = splitProps(props, ["label", "value", "delta", "trend", "hint", "class"])

  return (
    <div
      data-registry-id="display.kpi-stat"
      classList={{
        "flex flex-col gap-1 min-w-0": true,
        [local.class ?? ""]: !!local.class,
      }}
      {...rest}
    >
      <div class="text-12-medium uppercase tracking-[0.08em] text-text-weak truncate">{local.label}</div>
      <div class="text-24-medium text-text-strong leading-tight truncate font-display">
        {local.value}
      </div>
      <Show when={local.delta || local.hint}>
        <div class="flex items-center gap-1.5 text-13-regular text-text-base">
          <Show when={local.delta && local.trend}>
            <span
              aria-hidden="true"
              classList={{
                [trendClass[local.trend ?? "flat"]]: true,
                "tabular-nums": true,
              }}
            >
              {trendGlyph[local.trend ?? "flat"]} {local.delta}
            </span>
          </Show>
          <Show when={local.delta && !local.trend}>
            <span class="tabular-nums">{local.delta}</span>
          </Show>
          <Show when={local.hint}>
            <span class="text-text-weak truncate">{local.hint}</span>
          </Show>
        </div>
      </Show>
    </div>
  )
}
